import React from "react";

import AddIcon from "@mui/icons-material/Add";
import { Avatar, Box, Divider, Theme, Typography } from "@mui/material";
import { useLocation } from "react-router-dom";
import { makeStyles, withStyles } from "tss-react/mui";

import NavLink from "@/components/NavLink";
import { graphql } from "@/gql";
import { useFragment } from "@/hooks/useFragment";
import { formatISODate } from "@/utils/datetime";

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    marginBlock: 12,
  },
  bold: {
    fontWeight: "bold",
  },
  link: {
    display: "flex",
    alignItems: "center",
    textDecoration: "none",
    color: theme.palette.primary.main,
  },
}));

const StyledAddIcon = withStyles(AddIcon, {
  root: {
    width: 12,
    height: 12,
    marginRight: 8,
  },
});

type ClientCardProp = {
  clientId: string | undefined;
};

const clientCardFragment = graphql(`
  fragment ClientCard on Client {
    id
    profile {
      id
      fullName
      avatarUrl(variant: THUMB)
    }
    programForDate(date: $currentDate) {
      id
      name
      startDate
      endDate
    }
  }
`);

const ClientCard: React.FC<ClientCardProp> = ({ clientId }) => {
  const { pathname } = useLocation();

  const [client] = useFragment(clientCardFragment, `Client:${clientId}`, {
    variables: { currentDate: formatISODate(new Date()) },
  });

  const { classes } = useStyles();
  const planName = client?.programForDate?.name ?? "No Plan";
  return (
    <Box className={classes.root}>
      <Box paddingX={3} paddingY={2} width="100%">
        <Box display="flex" alignItems="center" width="100%">
          <Box display="flex" alignItems="center" width="30%">
            <Avatar src={client?.profile?.avatarUrl ?? ""} />
            <Box marginX={2}>
              <Typography className={classes.bold}>
                {client?.profile?.fullName}
              </Typography>
            </Box>
          </Box>
          <Divider orientation="vertical" flexItem style={{ width: 3 }} />
          <Box width="60%" marginLeft={8}>
            <Box display="flex" alignItems="center">
              <Typography className={classes.bold}>{planName}</Typography>
              <Box marginRight={4} />
              <NavLink
                className={classes.link}
                to={`${pathname}/${client?.id}/programs`}
              >
                <StyledAddIcon />
                <Typography variant="caption">Assign now</Typography>
              </NavLink>
            </Box>
            <Box marginY={1} />
            {client?.programForDate && (
              <Box display="flex" alignItems="center">
                <Box display="flex" alignItems="center">
                  <Typography variant="caption" color="textSecondary">
                    Start
                  </Typography>
                  <Box marginX={1} />
                  <Typography variant="caption" className={classes.bold}>
                    {client.programForDate.startDate}
                  </Typography>
                </Box>
                <Box marginX={1} />
                <Box display="flex" alignItems="center">
                  <Typography variant="caption" color="textSecondary">
                    End
                  </Typography>
                  <Box marginX={1} />
                  <Typography variant="caption" className={classes.bold}>
                    {client.programForDate.endDate}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ClientCard;
